import React, { useState, useEffect, useRef } from 'react';
import { Marker } from '@citiguide/shared';
import { Button } from '@citiguide/ui-components';
import { resolveMediaUrl, resolveMediaUrls } from '../../utils/mediaResolver';
import { markerService } from '../../services/markerService';
import { buildPoiAttributeRows, displayText } from './poiAttributes';

const CSS = `
.pd { display: flex; flex-direction: column; height: 100%; min-height: 0; background: var(--surface-card); font-family: var(--font-sans); position: relative; }
.pd__head { flex: none; padding: 14px 18px 12px; border-bottom: 1px solid var(--border-subtle); display: flex; flex-direction: column; gap: 9px; }
.pd__back { display: inline-flex; align-items: center; gap: 6px; font-size: var(--text-sm); font-weight: 600; color: var(--brand-primary); cursor: pointer; align-self: flex-start; background: none; border: none; padding: 0; font-family: inherit; }
.pd__headtop { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.pd__chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: var(--radius-pill); font-size: var(--text-2xs); font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #fff; background: var(--_c, var(--brand-primary)); }
.pd__chip-dot { width: 6px; height: 6px; border-radius: 50%; background: #fff; }
.pd__close { width: 32px; height: 32px; border-radius: var(--radius-pill); border: 1px solid var(--border-default); background: #fff; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; color: var(--text-muted); }
.pd__close:hover { background: var(--slate-50); }
.pd__title { font-size: var(--text-2xl); font-weight: 800; color: var(--text-heading); letter-spacing: var(--tracking-tight); line-height: 1.2; }
.pd__meta { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; font-size: var(--text-sm); color: var(--text-body); }
.pd__meta-item { display: inline-flex; align-items: center; gap: 5px; }
.pd__article { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 15px; padding: 16px 0 10px; }
.pd__article::-webkit-scrollbar { width: 0; }
.pd__p { margin: 0; padding: 0 18px; font-size: var(--text-base); line-height: var(--leading-relaxed); color: var(--text-body); }
.pd__p--lead { font-size: var(--text-lg); color: var(--text-strong); }
.pd__fig { margin: 0; }
.pd__fig img { width: 100%; display: block; background: var(--slate-200); }
.pd__fig--hero img { aspect-ratio: 16 / 10; object-fit: cover; }
.pd__fig--full img { aspect-ratio: 16 / 9; object-fit: cover; }
.pd__duo, .pd__trio { display: grid; gap: 2px; }
.pd__duo { grid-template-columns: 1fr 1fr; }
.pd__trio { grid-template-columns: 1fr 1fr 1fr; }
.pd__cell { position: relative; overflow: hidden; }
.pd__cell img { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; display: block; background: var(--slate-200); }
.pd__cell-ov { position: absolute; inset: 0; background: rgba(15,23,42,.56); color: #fff; display: flex; align-items: center; justify-content: center; font-size: var(--text-lg); font-weight: 700; }
.pd__accent { height: 4px; margin: 1px 18px 0; border-radius: 2px; background: var(--_c, var(--brand-primary)); opacity: .85; }
.pd__facts { border-top: 1px solid var(--border-default); margin-top: 3px; padding-top: 12px; }
.pd__facts-h { font-size: var(--text-2xs); font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: var(--text-muted); padding: 0 18px 9px; }
.pd__fact { display: flex; gap: 10px; align-items: flex-start; padding: 6px 18px; font-size: var(--text-sm); color: var(--text-body); line-height: 1.45; }
.pd__fact svg { color: var(--_c, var(--brand-primary)); margin-top: 2px; flex-shrink: 0; }
.pd__foot { flex: none; display: flex; gap: 8px; padding: 13px 18px; border-top: 1px solid var(--border-subtle); }
.pd__audio { margin: 0 18px; display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: var(--radius-lg); background: color-mix(in srgb, var(--_c, var(--brand-primary)) 8%, #fff); border: 1px solid color-mix(in srgb, var(--_c, var(--brand-primary)) 24%, #fff); }
.pd__audio-btn { flex: none; width: 40px; height: 40px; border-radius: 50%; background: var(--_c, var(--brand-primary)); color: #fff; display: inline-flex; align-items: center; justify-content: center; border: none; cursor: pointer; }
.pd__audio-body { flex: 1; min-width: 0; }
.pd__audio-t { font-size: var(--text-xs); font-weight: 700; color: var(--text-heading); }
.pd__audio-wave { display: flex; align-items: center; gap: 2px; height: 15px; margin-top: 5px; }
.pd__audio-wave span { flex: 1; border-radius: 1px; background: color-mix(in srgb, var(--_c, var(--brand-primary)) 45%, #fff); }
.pd__audio-wave span.on { background: var(--_c, var(--brand-primary)); }
.pd__audio-dur { flex: none; font-size: 11px; color: var(--text-muted); font-variant-numeric: tabular-nums; }
.pd__rating { cursor: pointer; border: none; background: none; font: inherit; padding: 2px 6px; margin: -2px -2px; border-radius: var(--radius-pill); transition: background .12s; display: inline-flex; align-items: center; gap: 5px; color: inherit; }
.pd__rating:hover { background: var(--slate-100); }
.pd__rate-cue { color: var(--brand-primary); font-weight: 700; margin-left: 3px; }
.pd__rate-ov { position: absolute; inset: 0; z-index: 20; background: rgba(255,255,255,.84); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; padding: 18px; }
@media (prefers-reduced-motion: no-preference) { .pd__rate-card { animation: pdRatePop .16s ease-out both; } }
@keyframes pdRatePop { from { transform: scale(.96); } to { transform: scale(1); } }
.pd__rate-card { width: 100%; max-width: 320px; background: var(--surface-card); border-radius: 18px; box-shadow: var(--shadow-overlay); border: 1px solid var(--border-default); padding: 20px 18px 18px; text-align: center; position: relative; }
.pd__rate-close { position: absolute; top: 9px; right: 9px; width: 28px; height: 28px; border-radius: 50%; border: none; background: transparent; color: var(--text-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.pd__rate-close:hover { background: var(--slate-100); }
.pd__rate-h { font-size: var(--text-lg); font-weight: 800; color: var(--text-heading); letter-spacing: -0.01em; }
.pd__rate-sub { font-size: var(--text-xs); color: var(--text-muted); margin-top: 2px; }
.pd__rate-stars { display: flex; justify-content: center; gap: 5px; margin: 15px 0 4px; }
.pd__star { border: none; background: none; cursor: pointer; padding: 0 2px; line-height: 0; color: var(--slate-300); font-size: 32px; transition: transform .1s, color .1s; }
.pd__star:hover { transform: scale(1.15); }
.pd__star.on { color: var(--amber-500); }
.pd__star.sm { font-size: 19px; cursor: default; }
.pd__star.sm:hover { transform: none; }
.pd__rate-word { font-size: var(--text-sm); font-weight: 700; color: var(--amber-600); height: 18px; }
.pd__rate-actions { display: flex; gap: 8px; margin-top: 14px; }
.pd__rate-actions button { flex: 1; padding: 10px; border-radius: var(--radius-md); font-family: inherit; font-size: var(--text-sm); font-weight: 700; cursor: pointer; border: 1px solid var(--border-default); }
.pd__rate-ghost { background: #fff; color: var(--text-body); }
.pd__rate-primary { background: var(--brand-primary); color: #fff; border-color: var(--brand-primary); }
.pd__rate-primary:disabled { opacity: .45; cursor: default; }
.pd__rate-check { width: 46px; height: 46px; border-radius: 50%; background: color-mix(in srgb, var(--green-500) 14%, #fff); color: var(--green-600); display: inline-flex; align-items: center; justify-content: center; margin: 0 auto 10px; }
`;

function injectStyles(): void {
  if (typeof document !== 'undefined' && !document.getElementById('cg-poidetail-css')) {
    const s = document.createElement('style');
    s.id = 'cg-poidetail-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

function categoryToCssColor(nameOrId: string): string {
  const s = (nameOrId || '').toLowerCase();
  if (/dine|food|eat|restaurant|cafe|bar|drink|bistro|tavern/.test(s)) return 'var(--cat-dining)';
  if (/cultur|art|museum|galleri|theatre|theater|heritage|monument/.test(s)) return 'var(--cat-culture)';
  if (/nature|park|garden|forest|outdoor|trail|beach|green/.test(s)) return 'var(--cat-nature)';
  if (/shop|store|market|mall|boutique|retail/.test(s)) return 'var(--cat-shopping)';
  if (/night|club|disco|lounge|pub|entertainment/.test(s)) return 'var(--cat-nightlife)';
  if (/transit|transport|bus|train|metro|airport|station/.test(s)) return 'var(--cat-transit)';
  if (/stay|hotel|hostel|accommodat|lodg/.test(s)) return 'var(--cat-stay)';
  if (/service|info|office|health|hospital|police|admin/.test(s)) return 'var(--cat-services)';
  return 'var(--brand-primary)';
}

const WAVE_HEIGHTS = [5, 9, 13, 7, 15, 10, 6, 12, 16, 9, 5, 11, 14, 8, 13, 6, 10, 15, 7, 12, 9, 5, 11, 8, 14, 6, 10, 7];

interface AudioSectionProps {
  src: string;
  title?: string;
}

function AudioSection({ src, title }: AudioSectionProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrent(el.currentTime);
    const onMeta = () => setDuration(el.duration);
    const onEnd = () => setPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); } else { el.play(); }
    setPlaying(!playing);
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? Math.round((current / duration) * WAVE_HEIGHTS.length) : 0;

  return (
    <div className="pd__audio">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button className="pd__audio-btn" type="button" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
        {playing
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 1 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
        }
      </button>
      <div className="pd__audio-body">
        <div className="pd__audio-t">{title || 'Audio guide'}</div>
        <div className="pd__audio-wave">
          {WAVE_HEIGHTS.map((h, i) => <span key={i} className={i < progress ? 'on' : ''} style={{ height: h }} />)}
        </div>
      </div>
      <span className="pd__audio-dur">{fmt(current)} / {duration > 0 ? fmt(duration) : '--:--'}</span>
    </div>
  );
}

interface RatingFlowProps {
  markerId: string;
  title: string;
  onClose: () => void;
}

function RatingFlow({ markerId, title, onClose }: RatingFlowProps) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const WORDS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];
  const shown = hover || stars;

  const handleSubmit = async () => {
    if (!stars) return;
    setSubmitting(true);
    try {
      await markerService.rateMarker(markerId, stars);
      setDone(true);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="pd__rate-ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pd__rate-card">
        <button className="pd__rate-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {!done ? (
          <>
            <div className="pd__rate-h">Rate your visit</div>
            <div className="pd__rate-sub">{title}</div>
            <div className="pd__rate-stars" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" className={'pd__star' + (shown >= i ? ' on' : '')}
                  onMouseEnter={() => setHover(i)} onClick={() => setStars(i)} aria-label={`${i} stars`}>
                  ★
                </button>
              ))}
            </div>
            <div className="pd__rate-word">{WORDS[shown]}</div>
            <div className="pd__rate-actions">
              <button type="button" className="pd__rate-ghost" onClick={onClose}>Not now</button>
              <button type="button" className="pd__rate-primary" disabled={!stars || submitting} onClick={handleSubmit}>
                {submitting ? 'Submitting…' : 'Submit rating'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pd__rate-check">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="pd__rate-h">Thanks for rating!</div>
            <div className="pd__rate-stars">
              {[1, 2, 3, 4, 5].map((i) => (
                <span key={i} className={'pd__star sm' + (stars >= i ? ' on' : '')}>★</span>
              ))}
            </div>
            <div className="pd__rate-sub">Your rating helps other visitors discover great places.</div>
            <div className="pd__rate-actions">
              <button type="button" className="pd__rate-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export interface PoiDetailProps {
  marker: Marker;
  lang: string;
  variant?: 'panel' | 'sheet';
  categoryLabel?: string;
  onClose?: () => void;
  onBack?: () => void;
  onRouteRequest?: (type: 'walking' | 'driving') => void;
}

export default function PoiDetail({ marker, lang, variant = 'panel', categoryLabel, onClose, onBack, onRouteRequest }: PoiDetailProps) {
  injectStyles();

  const content = marker.content?.[lang] ?? {};
  const title = content.title || '';
  const summary = content.summary || '';
  const description = content.description || '';
  const plusInfo = content.plusInfo || '';
  const category = marker.categories?.[0] ?? '';
  const catColor = categoryToCssColor(category);
  const catLabel = categoryLabel || category || '';

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [rateOpen, setRateOpen] = useState(false);

  useEffect(() => {
    const imageIds: string[] = content.images?.length
      ? content.images
      : content.image
        ? [content.image]
        : [];

    if (imageIds.length) {
      resolveMediaUrls(imageIds).then((urls) => setImageUrls(urls.filter((u): u is string => !!u)));
    } else {
      setImageUrls([]);
    }

    if (content.audio) {
      resolveMediaUrl(content.audio).then(setAudioUrl);
    } else {
      setAudioUrl(null);
    }
  }, [marker.markerId, lang]);

  const n = imageUrls.length;
  const facts = plusInfo.split('·').map((s) => s.trim()).filter(Boolean);
  const rating = marker.rating;

  const blocks: React.ReactNode[] = [];

  if (summary) blocks.push(<p className="pd__p pd__p--lead" key="lead">{summary}</p>);

  if (audioUrl) blocks.push(<AudioSection key="audio" src={audioUrl} title={title} />);

  if (n === 0) {
    blocks.push(<div className="pd__accent" key="acc" />);
    if (description) blocks.push(<p className="pd__p" key="desc">{description}</p>);
  } else {
    blocks.push(
      <figure className="pd__fig pd__fig--hero" key="hero">
        <img src={imageUrls[0]} alt={title} />
      </figure>
    );
    if (description) blocks.push(<p className="pd__p" key="desc">{description}</p>);
    const rest = imageUrls.slice(1);
    if (rest.length === 1) {
      blocks.push(<figure className="pd__fig pd__fig--full" key="f"><img src={rest[0]} alt="" /></figure>);
    } else if (rest.length === 2) {
      blocks.push(
        <div className="pd__duo" key="duo">
          {rest.map((src, i) => <div className="pd__cell" key={i}><img src={src} alt="" /></div>)}
        </div>
      );
    } else if (rest.length === 3) {
      blocks.push(
        <div className="pd__trio" key="trio">
          {rest.map((src, i) => <div className="pd__cell" key={i}><img src={src} alt="" /></div>)}
        </div>
      );
    } else {
      blocks.push(
        <div className="pd__trio" key="gallery">
          <div className="pd__cell"><img src={rest[0]} alt="" /></div>
          <div className="pd__cell"><img src={rest[1]} alt="" /></div>
          <div className="pd__cell">
            <img src={rest[2]} alt="" />
            <div className="pd__cell-ov">+{rest.length - 2}</div>
          </div>
        </div>
      );
    }
  }

  if (facts.length) {
    blocks.push(
      <div className="pd__facts" key="facts">
        <div className="pd__facts-h">Good to know</div>
        {facts.map((f, i) => (
          <div className="pd__fact" key={i}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {f}
          </div>
        ))}
      </div>
    );
  }

  const attributeRows = buildPoiAttributeRows(marker);
  if (attributeRows.length) {
    blocks.push(
      <div className="pd__facts" key="practical" data-testid="practical-info">
        <div className="pd__facts-h">Practical info</div>
        {attributeRows.map((row) => (
          <div className="pd__fact" key={row.key}>
            <strong>{row.label}</strong>
            <span>{displayText(row)}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="pd" style={{ '--_c': catColor } as React.CSSProperties}>
      <div className="pd__head">
        {variant === 'sheet' && onBack && (
          <button className="pd__back" onClick={onBack} type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All places
          </button>
        )}
        <div className="pd__headtop">
          {catLabel && (
            <span className="pd__chip"><span className="pd__chip-dot" />{catLabel}</span>
          )}
          {variant === 'panel' && onClose && (
            <button className="pd__close" onClick={onClose} aria-label="Close" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className="pd__title">{title}</div>
        <div className="pd__meta">
          <button type="button" className="pd__meta-item pd__rating" onClick={() => setRateOpen(true)} title="Rate this place">
            <svg width="15" height="15" viewBox="0 0 24 24" fill={rating ? 'var(--amber-500)' : 'none'} stroke="var(--amber-500)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            {rating ? rating.average.toFixed(1) : null}
            <span className="pd__rate-cue">{rating ? '· Rate' : 'Rate this place'}</span>
          </button>
        </div>
      </div>

      <div className="pd__article">{blocks}</div>

      {onRouteRequest && (
        <div className="pd__foot">
          <Button block onClick={() => onRouteRequest('walking')}>Directions</Button>
          <Button variant="secondary" onClick={() => onRouteRequest('driving')}>Drive</Button>
        </div>
      )}

      {rateOpen && (
        <RatingFlow
          markerId={marker.markerId}
          title={title}
          onClose={() => setRateOpen(false)}
        />
      )}
    </div>
  );
}
